import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Autocomplete,
  Avatar,
  Box,
  InputAdornment,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useFormikContext } from "formik";
import { useMemo } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../api/hooks/useColors";
import { platformUsersKeys } from "../../../../../../api/queryKeys";
import CustomToolTip from "../../../../../../components/CustomTooltip";
import LoadingIcon from "../../../../../../components/LoadingIcon";
import AnchorPopper from "../../../../../../components/forms/AnchorPopper";
import {
  getTextFieldInputProps,
  StyledTextField,
  textFieldLabelProps,
} from "../../../../../../components/forms/styles/formFieldStyles";

const JiraReporter = ({ options, showOptions, jsmIsAgent = true }) => {
  const { values, errors, touched, handleBlur, setFieldValue } =
    useFormikContext();
  const colors = useColors();
  const { getPlatformUser } = useAPI();

  const { data: platformUser } = useQuery({
    queryKey: platformUsersKeys.user(),
    queryFn: getPlatformUser,
    select: (response) => response.data,
  });

  const hasError = !!touched.reporter && !!errors.reporter;
  const errorMessage =
    hasError && typeof errors.reporter === "string" ? errors.reporter : "";

  const reporterLabel = (
    <Typography variant="caption">Raise this request on behalf of</Typography>
  );

  // Use displayName-only deduplication to guarantee no visual duplicates
  const dedupedOptions = useMemo(() => {
    if (!options || !options.length) return [];

    // First filter out invalid options (null, undefined, or displayName starting with "null")
    const validOptions = options.filter(
      (option) =>
        option &&
        option.displayName &&
        !option.displayName.toLowerCase().startsWith("null")
    );

    // Use displayName as the primary key for deduplication
    const uniqueNames = new Map();

    // Collect by displayName
    validOptions.forEach((option) => {
      if (!uniqueNames.has(option.displayName)) {
        uniqueNames.set(option.displayName, option);
      }
    });

    return Array.from(uniqueNames.values());
  }, [options]);

  // Custom filter logic focusing on exact matches
  const filterOptions = (options, { inputValue }) => {
    if (!inputValue || inputValue.length < 1) return options;

    const searchTerm = inputValue.toLowerCase().trim();

    return options.filter((option) => {
      if (!option || !option.displayName) return false;

      const displayName = option.displayName.toLowerCase();
      return displayName.includes(searchTerm);
    });
  };

  if (values.reporter === null) {
    const userEmail = platformUser?.original_email
      ? String(platformUser.original_email).toLowerCase()
      : null;
    if (!userEmail || !options?.length) return;

    const matchingReporter = options.find(
      (option) =>
        option?.emailAddress &&
        String(option.emailAddress).toLowerCase() === userEmail
    );

    if (matchingReporter) {
      setFieldValue("reporter", matchingReporter, false);
    }
  }

  const autocompleteComponent = (
    <Autocomplete
      options={dedupedOptions || []}
      disabled={!showOptions || !jsmIsAgent}
      getOptionLabel={(option) => option?.displayName || ""}
      filterOptions={filterOptions}
      value={values.reporter}
      onChange={(_, newValue) => setFieldValue("reporter", newValue)}
      popupIcon={<ExpandMoreIcon />}
      PopperComponent={AnchorPopper}
      isOptionEqualToValue={(option, value) =>
        option?.displayName === value?.displayName
      }
      renderOption={(props, option) => (
        <Box
          component="li"
          sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
          {...props}
        >
          <Avatar
            src={option.avatarUrl}
            alt={option.displayName}
            sx={{ width: 16, height: 16 }}
          >
            <Typography variant="caption">
              {option.displayName?.charAt(0)}
            </Typography>
          </Avatar>
          <Typography variant="caption">{option.displayName}</Typography>
        </Box>
      )}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          margin="dense"
          label={reporterLabel}
          fullWidth
          required={jsmIsAgent}
          error={hasError}
          helperText={errorMessage}
          onBlur={handleBlur}
          InputProps={{
            ...getTextFieldInputProps(params.InputProps),
            startAdornment: values.reporter?.avatarUrl && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  ml: 0.5,
                }}
              >
                <Avatar
                  src={values.reporter.avatarUrl}
                  alt={values.reporter.displayName}
                  sx={{ width: 16, height: 16, mr: 1 }}
                >
                  {values.reporter.displayName?.charAt(0)}
                </Avatar>
              </Box>
            ),
            endAdornment: (
              <Box>
                {!showOptions && (
                  <CustomToolTip
                    title={
                      <Typography variant="caption">
                        Loading... This may take up to 10 seconds
                      </Typography>
                    }
                    placement="top"
                  >
                    <InputAdornment position="end">
                      <LoadingIcon
                        size={20}
                        sx={{ color: colors.text_disabled }}
                      />
                    </InputAdornment>
                  </CustomToolTip>
                )}
                {params.InputProps.endAdornment}
              </Box>
            ),
          }}
          InputLabelProps={textFieldLabelProps(params.InputLabelProps)}
        />
      )}
    />
  );

  // If not an agent, wrap with tooltip explaining why it's disabled
  if (!jsmIsAgent) {
    return (
      <CustomToolTip
        title={
          <Typography variant="caption">
            Reporter selection is only available for Jira Service Management
            users with agent permissions
          </Typography>
        }
        placement="left"
      >
        <Box>{autocompleteComponent}</Box>
      </CustomToolTip>
    );
  }

  return autocompleteComponent;
};
export default JiraReporter;
